import { useEffect, useState } from "react";
import { Star, Plus, X, Loader2, Sparkles, TrendingUp, TrendingDown, Lock } from "lucide-react";
import axios from "axios";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n, digits = 2) =>
  n == null ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const SUGGESTIONS = ["AAPL", "MSFT", "NVDA", "TSLA", "GOOGL", "AMZN", "META", "AMD", "BTC-USD", "ETH-USD", "GC=F", "CL=F", "^GSPC"];

export default function WatchlistPanel({ onOpenBrief }) {
  const email = getStoredEmail();
  const [data, setData] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!email) { setData(null); return; }
    try {
      const r = await axios.get(`${API}/watchlist/${email}`, { timeout: 20000 });
      setData(r.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [email]);

  const mutate = async (action, symbol) => {
    setErr("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/watchlist`, { email, symbol, action });
      setData((d) => ({ ...(d || {}), symbols: r.data.symbols }));
      setInput("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update watchlist.");
    } finally { setBusy(false); }
  };

  const refreshCommentary = async () => {
    setErr("");
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/watchlist/${email}/commentary`, {}, { timeout: 30000 });
      setData((d) => ({ ...(d || {}), commentary: r.data }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not generate commentary.");
    } finally { setAiBusy(false); }
  };

  if (!email) {
    return (
      <div className="rounded-xl border border-[var(--line)] bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-5" data-testid="watchlist-anon">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">
          <Star size={12} /> Your Watchlist
        </div>
        <p className="text-[14px] mt-2 leading-snug opacity-90">
          Subscribe free to pin tickers and get a calm, AI-written daily commentary on what moved — and why.
        </p>
        <button onClick={onOpenBrief} className="btn gold mt-3 w-full justify-center" data-testid="watchlist-anon-cta">
          <Lock size={12} /> Unlock free
        </button>
      </div>
    );
  }

  const symbols = data?.symbols || [];
  const quotes = data?.quotes || [];
  const commentary = data?.commentary;

  return (
    <div className="rounded-xl border border-[var(--line)] bg-white p-4" data-testid="watchlist-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-black text-[var(--blue)]">
          <Star size={12} /> Your Watchlist
        </div>
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">
          {symbols.length}/25 tickers
        </span>
      </div>

      {/* Add ticker input */}
      <form
        onSubmit={(e) => { e.preventDefault(); if (input.trim()) mutate("add", input.trim().toUpperCase()); }}
        className="flex gap-2 mb-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase())}
          placeholder="Add ticker — AAPL, BTC-USD, GC=F, ^GSPC…"
          maxLength={16}
          className="field flex-1 text-[13px]"
          data-testid="watchlist-input"
        />
        <button type="submit" disabled={busy || !input.trim()} className="btn primary" data-testid="watchlist-add-btn">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
        </button>
      </form>

      {/* Suggestions when empty */}
      {symbols.length === 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)] mb-1">Quick add</div>
          <div className="flex flex-wrap gap-1">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => mutate("add", s)} disabled={busy}
                      className="text-[11px] px-2 py-1 rounded border border-[var(--line)] hover:bg-[var(--sky)] hover:border-[var(--blue)] transition"
                      data-testid={`watchlist-suggest-${s}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quotes */}
      {quotes.length > 0 && (
        <div className="border-t border-[var(--line)] -mx-4 px-4 pt-2" data-testid="watchlist-quotes">
          {quotes.map((q) => {
            const up = (q.change_pct ?? 0) >= 0;
            return (
              <div key={q.symbol} className="flex items-center justify-between gap-3 py-2 border-b border-[var(--line)] last:border-0">
                <div className="min-w-0">
                  <div className="text-[13px] font-black tracking-tight text-[var(--ink)]">{q.symbol}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)] truncate">{q.label && q.label !== q.symbol ? q.label : "\u00A0"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[14px] font-black tabular-nums text-[var(--ink)]">{fmt(q.price, q.price > 1000 ? 0 : 2)}</div>
                  <div className={`text-[11px] font-bold tabular-nums flex items-center justify-end gap-0.5 ${up ? "text-emerald-600" : "text-red-600"}`}>
                    {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {up ? "+" : ""}{fmt(q.change_pct, 2)}%
                  </div>
                </div>
                <button onClick={() => mutate("remove", q.symbol)} className="text-[var(--muted)] hover:text-red-600" aria-label={`remove ${q.symbol}`} data-testid={`watchlist-remove-${q.symbol}`}>
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* AI Commentary */}
      {symbols.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--line)]" data-testid="watchlist-commentary">
          {commentary ? (
            <div className="bg-[var(--sky)] rounded-md p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--blue)] flex items-center gap-1">
                  <Sparkles size={11} /> AI Daily Take
                </div>
                <button onClick={refreshCommentary} disabled={aiBusy} className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--blue)] hover:underline" data-testid="watchlist-refresh-ai">
                  {aiBusy ? "Refreshing…" : "Refresh"}
                </button>
              </div>
              <p className="text-[13px] text-[var(--ink)] leading-snug">{commentary.text}</p>
            </div>
          ) : (
            <button onClick={refreshCommentary} disabled={aiBusy} className="btn gold w-full justify-center" data-testid="watchlist-generate-ai">
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <><Sparkles size={12} /> Generate AI Daily Take</>}
            </button>
          )}
        </div>
      )}

      {err && <div className="text-[12px] text-red-600 mt-2" data-testid="watchlist-error">{err}</div>}
    </div>
  );
}
